import { PeerType } from '@yandex-360/telemost-sdk/models';

import { RootState } from '../../../../../../init-store';
import { SELF } from '../../../../../shared/constants';
import { isDevelopmentEnvironmentSelector, isRoomAgentMode } from '../../../../../shared/features/config/selectors';
import { selectIsAloneScreenEnabled } from '../../../../../shared/features/flags/selectors';
import { selectIsCommandListOpen } from '../../../../../shared/features/room-agent/dtmf/command-list/selectors';
import {
    selectIsDebugMenuOpen,
    selectIsDrawConferenceDisabled,
} from '../../../../../shared/features/room-agent/dtmf/debug-menu/selectors';
import { selectIsParticipantListOpen } from '../../../../../shared/features/room-agent/dtmf/participant-list/selectors';
import { isMobileUa } from '../../../../../shared/features/uatraits/selectors';
import { getFontSizeByWidth } from '../../../../../shared/helpers/avatar-placeholder';
import { getCssCustomProperty } from '../../../../../shared/helpers/get-css-custom-property';
import { getReactionIconSizes } from '../../../../../shared/helpers/get-reaction-icon-sizes';
import { DEFAULT_RATIO, OVERLAY_GAP } from '../../../../../shared/helpers/layout-manager/constants';
import { getGridLayout } from '../../../../../shared/helpers/layout-manager/grid-layout';
import {
    getPresenterDrawLayout,
    getPresenterItemSize,
} from '../../../../../shared/helpers/layout-manager/presenter-draw-layout';
import { Area, Size } from '../../../../../shared/helpers/layout-manager/types';
import { RolePermission } from '../../../../../shared/helpers/permissions';
import { GRID_VIEW } from '../../../constants';
import { isVideoReadyForDraw } from '../../../helpers/is-video-ready-for-draw';
import {
    canChangePage,
    getDesktopSharingStatus,
    getDominantSpeaker,
    getFormattedCallNumber,
    getParticipantsBySettings,
    getScreenSharingParticipant,
    getVisibleParticipants,
    selectIsNewGrid,
    selectIsPermissionsExtendedEnabled,
    selectIsPresenterView,
    selectMeetingName,
    selectVideoGrid,
} from '../../../selectors';
import { Participant, RecordSagaHelpers, StreamToVideosMap } from '../../../types';
import { hasRolePermission } from '../../permissions';
import { getParticipantRole } from '../../permissions/helpers';
import { drawAloneScreen } from './draw-alone-screen';
import { DisplayMode, drawMedia, safeDrawMedia } from './draw-helpers';
import { getAvatarGuestImage, getAvatarImage, getAvatarPlaceholderImage, getIconImage, getImage } from './draw-images';
import { drawParticipantReaction } from './draw-participant-reaction';
import { drawParticipantStatuses } from './draw-participant-statuses';
import { drawCanvasNotifications, selectActiveNotificationTypes } from './dtmf/canvas-notifications';
import { drawCommandList } from './dtmf/command-list';
import { drawDebugMenu } from './dtmf/debug-menu';
import { drawParticipantList } from './dtmf/participant-list';

const PRESENTER_MAX_COUNT = 6;

interface DrawParticipantInfoOptions {
    hasMicrophonePermission?: boolean;
    isPermissionsExtended?: boolean;
}

const drawParticipantInfo = (
    ctx: CanvasRenderingContext2D,
    area: Area,
    participant: Participant,
    { hasMicrophonePermission = true, isPermissionsExtended }: DrawParticipantInfoOptions = {}
) => {
    const padding = { horizontal: 6, vertical: 3 };
    const showIcon = !participant.audioTrackState || participant.audioTrackState.muted;

    if (!showIcon && !participant.displayName) {
        return;
    }

    const baseSize = 16; // Высота и иконки, и текста - 16px
    const gapSize = 4; // Расстояние между иконкой и текстом

    const iconSpaceWidth = showIcon ? baseSize + gapSize : 0; // Пространство для иконки

    let textDesiredWidth = 0;

    if (participant.displayName) {
        const font = getCssCustomProperty('--typography-font-family');
        ctx.font = '14px ' + font;
        ctx.textBaseline = 'bottom';
        const textMetrics = ctx.measureText(participant.displayName);
        textDesiredWidth = textMetrics.width;
    }

    const contentDesiredWidth = iconSpaceWidth + textDesiredWidth; // Желаемый размер контента

    // Область всего блока
    const blockHeight = baseSize + padding.vertical * 2;
    const blockArea = {
        x: area.x + area.width / 2 - Math.min(contentDesiredWidth + padding.horizontal * 2, area.width) / 2,
        y: area.y + area.height - blockHeight,
        width: Math.min(contentDesiredWidth + padding.horizontal * 2, area.width),
        height: baseSize + padding.vertical * 2,
    };

    // Область контента (иконка + текст)
    const contentArea = {
        x: blockArea.x + padding.horizontal,
        y: blockArea.y + padding.vertical,
        width: blockArea.width - padding.horizontal * 2,
        height: blockArea.height - padding.vertical * 2,
    };

    // Рисуем подложку
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(
        contentArea.x - padding.horizontal,
        contentArea.y - padding.vertical,
        blockArea.width,
        blockArea.height
    );

    // Рисуем текст
    if (participant.displayName) {
        const realTextWidth = contentArea.width - iconSpaceWidth;

        ctx.fillStyle = 'white';
        const textArea = {
            x: contentArea.x,
            y: contentArea.y,
            width: realTextWidth,
            height: contentArea.height,
        };

        const isCropping = textDesiredWidth !== realTextWidth;

        // Нужно ли обрезать текст
        if (isCropping) {
            // Чтобы обрезать длинный текст, мы должны наложить на канвас ограничивающую область. Для этого
            // сохранить текущее состояние, задать ограничивающий path, кропнуть канвас, нарисовать текст и
            // вернуться к сохраненному состоянию
            ctx.save();
            const p = new Path2D();
            p.rect(textArea.x, textArea.y, textArea.width, textArea.height);
            ctx.clip(p);
        }

        // Текст рисуется по нижней границе
        ctx.fillText(participant.displayName, textArea.x, textArea.y + textArea.height);

        if (isCropping) {
            ctx.restore();
        }
    }

    // Рисуем иконку
    if (showIcon) {
        const muteArea = {
            x: contentArea.x + (participant.displayName ? contentArea.width - baseSize + gapSize : 0),
            y: contentArea.y,
            width: baseSize,
            height: baseSize,
        };
        safeDrawMedia(
            ctx,
            muteArea,
            getIconImage({
                name: isPermissionsExtended && !hasMicrophonePermission ? 'micBlocked' : 'micMuted',
                iconColor: '#fff',
            }),
            { mode: 'contain' }
        );
    }
};

const drawDominant = (ctx: CanvasRenderingContext2D, area: Area) => {
    const lineWidth = 4;
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = getComputedStyle(ctx.canvas).getPropertyValue('--orb-surface-brand');

    const gap = lineWidth / 2;
    ctx.strokeRect(area.x + gap, area.y + gap, area.width - lineWidth, area.height - lineWidth);
};

const drawParticipantStub = (
    ctx: CanvasRenderingContext2D,
    area: Area,
    avatarUrl: Participant['avatarUrl'],
    avatarPlaceholder: Participant['avatarPlaceholder'],
    shouldRenderAvatar: Participant['shouldRenderAvatar'],
    isPresenter: boolean,
    isGuest: boolean,
    peerType?: PeerType
) => {
    const avatarSpacePadding = 32;
    const textNamePadding = 22;
    // Подложка
    ctx.fillStyle = 'rgba(255, 255, 255, .06)';
    /**
     * Итоговые размеры иконки считаются так:
     * В режиме галереи:
     * - берется поле из размера основного контейнера с отступами avatarSpacePadding px,
     * плюс снизу еще отступ для текста textNamePadding px
     * Это будут у нас containerIconWidth и containerIconHeight.
     * - в этом контейнере берется минимальное значение - ширины или высоты
     * - если значение меньше 64 пикселей, устанавливаем иконку размером 64x64px
     * В режиме докладчика:
     * у верхнего ряда миниатюр:
     * - размер иконки 64x64px
     * - оступы у контейнера не берутся, отступ от текста не нужен, важно расположить посередине
     */
    const containerIconWidth = area.width - (avatarSpacePadding + avatarSpacePadding);
    const containerIconHeight = area.height - (avatarSpacePadding + textNamePadding + avatarSpacePadding);
    ctx.fillRect(area.x, area.y, area.width, area.height);

    let imgSize = Math.min(containerIconWidth, containerIconHeight, 300);
    if (imgSize < 64) {
        imgSize = 64;
    }

    ctx.save();
    ctx.beginPath();
    // рисуем круг по центру области диаметром с размер нужной нам заглушки
    ctx.arc(
        area.x + area.width / 2,
        isPresenter ? area.y + area.height / 2 - textNamePadding / 2 : area.y + area.height / 2,
        imgSize / 2,
        0,
        Math.PI * 2,
        true
    );
    ctx.closePath();
    // stroke заканчивает рисование круга.
    // Надо выставить свой цвет линии, рисующей круг, иначе применится цвет обводки, уже определенный где-то ранее.
    ctx.strokeStyle = 'transparent';
    ctx.stroke();
    ctx.clip();
    if (!shouldRenderAvatar) {
        ctx.fillStyle = avatarPlaceholder.backgroundColor;
    }

    let scaleIconFactor = 1;
    const isPeerConnectedByPhoneCall = peerType === PeerType.INCOMING_PHONE_CALL;
    if (isPeerConnectedByPhoneCall) {
        scaleIconFactor = 0.3;
    }

    const xBeginIconArea = area.width / 2 - (imgSize * scaleIconFactor) / 2;
    const yBeginIconArea = area.height / 2 - (imgSize * scaleIconFactor) / 2;
    ctx.fillRect(area.x, area.y, area.width, area.height);

    const fontSize = getFontSizeByWidth(imgSize);

    safeDrawMedia(
        ctx,
        {
            x: area.x + xBeginIconArea,
            y: area.y + yBeginIconArea - (isPresenter ? textNamePadding / 2 : 0),
            width: imgSize * scaleIconFactor,
            height: imgSize * scaleIconFactor,
        },
        shouldRenderAvatar
            ? getAvatarImage(avatarUrl, imgSize)
            : isGuest
              ? isPeerConnectedByPhoneCall
                  ? getIconImage({
                        name: 'callCalling',
                        iconColor: avatarPlaceholder.textColor,
                    })
                  : getAvatarGuestImage(avatarPlaceholder)
              : getAvatarPlaceholderImage({
                    placeholder: avatarPlaceholder,
                    size: imgSize,
                    fontSize,
                }),
        {
            mode: 'contain',
            verticalAlign: 'end',
        }
    );

    ctx.restore();
};

type DrawParticipantOptions = {
    isDrawDominant?: boolean;
    isDrawDesktopTrack?: boolean;
    isPresenter?: boolean;
    isDrawPresenter?: boolean;
    shouldDrawParticipantStatuses?: boolean;
    participantsCount: number;
    gridView: GRID_VIEW;
    isMainSlot: boolean;
    isMobile: boolean;
    isNewGrid?: boolean;
    hasMicrophonePermission?: boolean;
    isPermissionsExtended?: boolean;
};

const isMediaStream = (srcObject: HTMLMediaElement['srcObject']): srcObject is MediaStream => {
    if (!srcObject) {
        return false;
    }

    return srcObject instanceof MediaStream;
};

const getVideoTrackIds = (video: HTMLVideoElement, track: MediaStreamTrack): string[] => {
    const trackIds = new Set<string>();
    const trackIdFromDataset = video.dataset.g_track_id;

    if (trackIdFromDataset) {
        trackIds.add(trackIdFromDataset);
    }

    if (track.id) {
        trackIds.add(track.id);
    }

    return [...trackIds];
};

const getPreferredVideoByTrackId = (
    currentVideo: Maybe<HTMLVideoElement>,
    candidateVideo: HTMLVideoElement
): HTMLVideoElement => {
    if (!currentVideo) {
        return candidateVideo;
    }

    if (isVideoReadyForDraw(currentVideo)) {
        return currentVideo;
    }

    if (isVideoReadyForDraw(candidateVideo)) {
        return candidateVideo;
    }

    return currentVideo;
};

const addTrackToStreamToVideosMap = (
    streamToVideosMap: StreamToVideosMap,
    track: MediaStreamTrack,
    video: HTMLVideoElement
) => {
    streamToVideosMap.byTrack.set(track, video);

    getVideoTrackIds(video, track).forEach((trackId) => {
        const mappedVideo = streamToVideosMap.byTrackId.get(trackId);
        streamToVideosMap.byTrackId.set(trackId, getPreferredVideoByTrackId(mappedVideo, video));
    });
};

const collectStreamToVideosMap = (): StreamToVideosMap => {
    const streamToVideosMap: StreamToVideosMap = {
        byTrack: new Map(),
        byTrackId: new Map(),
    };

    document.querySelectorAll<HTMLVideoElement>('video').forEach((video) => {
        if (!isMediaStream(video.srcObject)) {
            return;
        }

        const track = video.srcObject.getVideoTracks()[0];
        if (!track) {
            return;
        }

        addTrackToStreamToVideosMap(streamToVideosMap, track, video);
    });

    return streamToVideosMap;
};

const drawParticipant = (
    ctx: CanvasRenderingContext2D,
    area: Area,
    participant: Participant,
    mode: DisplayMode,
    streamToVideosMap: StreamToVideosMap,
    {
        isDrawDominant = true,
        isDrawDesktopTrack = true,
        isPresenter = false,
        isDrawPresenter = false,
        shouldDrawParticipantStatuses = false,
        participantsCount,
        gridView,
        isMainSlot,
        isMobile,
        isNewGrid = false,
        isPermissionsExtended,
        hasMicrophonePermission,
    }: DrawParticipantOptions,
    helpers: RecordSagaHelpers
) => {
    const { isDesktopTrack, video, hasFrameBorder, statusIcons } = helpers.getParticipantDrawerParams(
        participant,
        streamToVideosMap,
        isDrawDesktopTrack,
        isDrawPresenter
    );

    const isGuest = !participant.uid;
    const padding = !isNewGrid ? 4 : 0; //TODO нужно будет убрать padding во время того когда поменяем работу presenter сетки (https://st.yandex-team.ru/TELEMOSTFRONT-510)
    const drawArea: Area = {
        x: area.x + padding,
        y: area.y + padding,
        width: area.width - 2 * padding,
        height: area.height - 2 * padding,
    };
    let drawStub = true;

    if (video) {
        const canDrawVideo = !isDesktopTrack || isVideoReadyForDraw(video);

        if (canDrawVideo) {
            let isDesktopTrackContextSaved = false;

            try {
                if (isDesktopTrack) {
                    ctx.save();
                    isDesktopTrackContextSaved = true;
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                }

                drawMedia(ctx, drawArea, video, { isMirror: !isDesktopTrack && participant.id === SELF, mode });
                drawStub = false;
            } catch (error) {
                // Firefox может дать ошибку при отрисовке видео. Поэтому если что - отрисуем заглушку
                // eslint-disable-next-line no-console
                console.error(error);
            } finally {
                if (isDesktopTrackContextSaved) {
                    ctx.restore();
                }
            }
        }
    }

    if (drawStub) {
        drawParticipantStub(
            ctx,
            drawArea,
            participant.avatarUrl,
            participant.avatarPlaceholder,
            participant.shouldRenderAvatar,
            isPresenter,
            isGuest,
            participant.peerType
        );
    }

    drawParticipantInfo(ctx, drawArea, participant, { isPermissionsExtended, hasMicrophonePermission });

    if (shouldDrawParticipantStatuses && statusIcons) {
        drawParticipantStatuses(ctx, drawArea, statusIcons);
    }

    if (isDrawDominant && hasFrameBorder) {
        drawDominant(ctx, drawArea);
    }

    const iconSizes = getReactionIconSizes(gridView, isMainSlot, isMobile, participantsCount);

    drawParticipantReaction(ctx, drawArea, participant, iconSizes);
};
export const drawConference = (
    ctx: CanvasRenderingContext2D,
    size: Size,
    state: RootState,
    hideMyVideo: boolean,
    helpers: RecordSagaHelpers
) => {
    const colorBackgroundDefault = getCssCustomProperty('--color-mg-surface-submerged');
    const isNewGrid = selectIsNewGrid(state);
    const isScreenSharingActive = getDesktopSharingStatus(state) !== 'none';
    const isPermissionsExtended = selectIsPermissionsExtendedEnabled(state);
    const margin = isNewGrid ? 4 : 0;
    ctx.fillStyle = colorBackgroundDefault || '';
    ctx.fillRect(0, 0, size.width, size.height);

    if (!selectIsDrawConferenceDisabled(state)) {
        const { currentPage, activeGridView } = selectVideoGrid(state);

        const visibleParticipants = getVisibleParticipants(state);
        const isMobile = isMobileUa(state);
        const streamToVideosMap = collectStreamToVideosMap();

        let participantListLayout: Area[];
        if (activeGridView === GRID_VIEW.DEFAULT) {
            // Если кол-во участников не вмещается на одну страницу, то на других страницах
            // всегда участников показываем в сетке на 12 человек
            const layoutSize = currentPage > 0 ? 12 : visibleParticipants.length;

            const gridLayout = getGridLayout(
                size,
                visibleParticipants.length,
                layoutSize,
                DEFAULT_RATIO,
                isNewGrid,
                margin
            );
            participantListLayout = gridLayout.participantsListLayout;
        } else {
            const presenter = getScreenSharingParticipant(state) || getDominantSpeaker(state);
            const onlyPresenter = (() => {
                const count = visibleParticipants.length;
                const isOnlyParticipant = count === 1;
                const isMyHiddenVideoOnly = hideMyVideo && count === 0;
                if (isMyHiddenVideoOnly) {
                    return true;
                }
                const isSelfPresenter = Boolean(presenter) && presenter?.id === SELF;
                const isOnlySelfParticipant =
                    isOnlyParticipant && visibleParticipants[0].id === SELF && isSelfPresenter;
                const isPresenterOnly = isOnlySelfParticipant;
                return isPresenterOnly;
            })();

            const presenterLayout = getPresenterDrawLayout({
                size,
                count: visibleParticipants.length,
                maxCount: PRESENTER_MAX_COUNT,
                isPresenterOnly: onlyPresenter,
                isNewGrid,
                isScreenSharingActive,
                margin,
            });
            participantListLayout = presenterLayout.participantsListLayout;

            if (presenter && !(hideMyVideo && onlyPresenter)) {
                const role = getParticipantRole(presenter);
                const hasMicrophonePermission = hasRolePermission(state, RolePermission.MICROPHONE, role);
                drawParticipant(
                    ctx,
                    presenterLayout.presenterArea,
                    presenter,
                    'contain',
                    streamToVideosMap,
                    {
                        isDrawDominant: true,
                        isDrawPresenter: true,
                        shouldDrawParticipantStatuses: true,
                        participantsCount: visibleParticipants.length,
                        gridView: activeGridView,
                        isMainSlot: true,
                        isMobile,
                        isNewGrid,
                        isPermissionsExtended,
                        hasMicrophonePermission,
                    },
                    helpers
                );
            }
        }

        for (let i = 0; i < Math.min(visibleParticipants.length, participantListLayout.length); i++) {
            if (hideMyVideo && visibleParticipants[i].id === SELF) {
                continue;
            }

            const participant = visibleParticipants[i];
            const role = getParticipantRole(participant);
            const hasMicrophonePermission = hasRolePermission(state, RolePermission.MICROPHONE, role);
            drawParticipant(
                ctx,
                participantListLayout[i],
                participant,
                'cover',
                streamToVideosMap,
                {
                    isDrawDominant: true,
                    isDrawDesktopTrack: activeGridView === GRID_VIEW.DEFAULT,
                    isPresenter: activeGridView === GRID_VIEW.DEFAULT,
                    shouldDrawParticipantStatuses: true,
                    participantsCount: visibleParticipants.length,
                    gridView: activeGridView,
                    isMainSlot: false,
                    isMobile,
                    isNewGrid,
                    isPermissionsExtended,
                    hasMicrophonePermission,
                },
                helpers
            );
        }
    }

    const visibleParticipants = getParticipantsBySettings(state).length;
    if (
        selectIsAloneScreenEnabled(state) &&
        isRoomAgentMode(state) &&
        (!visibleParticipants || (isDevelopmentEnvironmentSelector(state) && visibleParticipants === 1))
    ) {
        drawAloneScreen(ctx, { meetingName: selectMeetingName(state), callNumber: getFormattedCallNumber(state) });
    }

    const activeNotificationTypes = selectActiveNotificationTypes(state);
    drawCanvasNotifications(ctx, activeNotificationTypes);

    if (selectIsCommandListOpen(state)) {
        drawCommandList(ctx, state);
    }

    if (selectIsParticipantListOpen(state)) {
        drawParticipantList(ctx, state);
    }

    if (selectIsDebugMenuOpen(state)) {
        drawDebugMenu(ctx, state);
    }

    if (isRoomAgentMode(state)) {
        drawPaginationArrows(ctx, state, size);
    }
};

function drawPaginationArrows(ctx: CanvasRenderingContext2D, state: RootState, size: Size) {
    const ARROW_SIZE = 88;
    const showPrevPageButton = canChangePage(state, -1);
    const showNextPageButton = canChangePage(state, 1);
    const isPresenterView = selectIsPresenterView(state);
    const nextImage = getImage('next');
    const isNewGrid = selectIsNewGrid(state);
    const isMobile = isMobileUa(state);
    const { top, left, right } = (() => {
        const cords = {
            top: ctx.canvas.height / 2 - ARROW_SIZE / 2,
            left: 5,
            right: ctx.canvas.width - 5 - ARROW_SIZE,
        };

        if (isPresenterView) {
            const { itemHeight } = getPresenterItemSize({
                size,
                maxCount: PRESENTER_MAX_COUNT,
                isNewGrid,
                isMobile,
            });
            cords.top = itemHeight / 2 - ARROW_SIZE / 2 + (isNewGrid ? OVERLAY_GAP : 0);
        }

        return cords;
    })();

    if (showPrevPageButton) {
        safeDrawMedia(
            ctx,
            {
                x: left,
                y: top,
                width: ARROW_SIZE,
                height: ARROW_SIZE,
            },
            nextImage,
            {
                mode: 'contain',
                verticalAlign: 'center',
                horizontalAlign: 'center',
                isMirror: true,
            }
        );
    }
    if (showNextPageButton) {
        safeDrawMedia(
            ctx,
            {
                x: right,
                y: top,
                width: ARROW_SIZE,
                height: ARROW_SIZE,
            },
            nextImage,
            {
                mode: 'contain',
                verticalAlign: 'center',
                horizontalAlign: 'center',
            }
        );
    }
}
